import { initializeApp, getApps } from 'firebase/app';
import { getAnalytics, isSupported, logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { hasConsent } from './consent/consentStore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const requiredConfigKeys = ['apiKey', 'authDomain', 'projectId', 'appId', 'measurementId'];
const hasRequiredConfig = requiredConfigKeys.every((key) => Boolean(firebaseConfig[key]));

let analyticsPromise;

function getFirebaseApp(){
  if(!hasRequiredConfig) return null;
  return getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
}

function getAnalyticsInstance(){
  if(!hasConsent('analytics')) return Promise.resolve(null);
  if(typeof window === 'undefined' || !hasRequiredConfig){
    return Promise.resolve(null);
  }

  if(!analyticsPromise){
    analyticsPromise = isSupported()
      .then((supported) => {
        if(!supported) return null;
        const app = getFirebaseApp();
        return app ? getAnalytics(app) : null;
      })
      .catch((err) => {
        if(process.env.NODE_ENV !== 'production'){
          console.warn('Firebase Analytics unavailable:', err);
        }
        return null;
      });
  }

  return analyticsPromise;
}

function sanitizeEventParams(params){
  return Object.entries(params || {}).reduce((safe, [key, value]) => {
    if(/email|name|message|body|subject/i.test(key)) return safe;
    if(value === undefined || value === null) return safe;
    safe[key] = value;
    return safe;
  }, {});
}

export function trackEvent(eventName, eventParams = {}){
  if(!eventName) return;

  getAnalyticsInstance().then((analytics) => {
    if(!analytics) return;
    logEvent(analytics, eventName, sanitizeEventParams(eventParams));
  });
}

export function initializeAnalytics(){
  getAnalyticsInstance();
}

export async function syncFirebaseAnalyticsConsent(consent){
  if(consent?.analytics === true){
    const analytics = await getAnalyticsInstance();
    if(analytics) setAnalyticsCollectionEnabled(analytics, true);
    return;
  }

  if(!analyticsPromise) return;
  const analytics = await analyticsPromise;
  if(analytics) setAnalyticsCollectionEnabled(analytics, false);
}

export function trackPageView(pagePath){
  if(typeof window === 'undefined') return;

  trackEvent('page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: pagePath || `${window.location.pathname}${window.location.search}${window.location.hash}`,
  });
}
